/**
 * @file api.ts
 * @brief React Query hooks for company-scoped Boardroom MeetingIntel.
 *
 * @details
 * Talks to the shared boardroom API (`/api/boardroom/*`), which the backend
 * widened so a company_admin/admin reads + manages any meeting owned by a
 * member of their company (08-07-2026). Read hooks: list (server-side
 * pagination/filtering) + detail. Manage mutations mirror the widened
 * write endpoints: reprocess-diarization, regenerate-intelligence,
 * export-mom, export-transcript, translate, and delete. (The transcript
 * translation call went 15-10-2026, ADR-037: the transcript stays original.)
 *
 * @dependencies
 * - @tanstack/react-query: caching + mutation lifecycle
 * - shared/api/client: thin axios wrappers (apiGet/apiPost/apiPut/apiDelete)
 * - shared/api/endpoints: boardroomEndpoints paths
 *
 * @references
 * - BACKEND/src/features/boardroom/routes.js
 * - features/company-meetings/schemas.ts (payload validation)
 * - features/company-sessions/api.ts (sibling pattern)
 *
 * @author DES
 * @date 08-07-2026
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { z } from "zod";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "@/shared/api/client";
import { boardroomEndpoints } from "@/shared/api/endpoints";
// Key shapes only — the super-admin page caches the same meeting under its own
// key, and the shared todo toggle has to keep both honest. Safe direction: that
// module imports this feature's *schemas*, never its api. (04-08-2026)
import { adminMeetingsKeys } from "@/features/admin-meetings/api";
import {
  ExportMomResponseSchema,
  ExportTranscriptResponseSchema,
  MeetingAttachedNotesResponseSchema,
  MeetingDetailResponseSchema,
  ToggleTodoResponseSchema,
  MeetingSearchQuotaResponseSchema,
  MeetingSearchResponseSchema,
  MeetingsListResponseSchema,
  TranslateMomResponseSchema,
  type CompanyMeetingsListParams,
  type ExportMomResponse,
  type ExportTranscriptResponse,
  type MeetingAttachedNote,
  type MeetingDetail,
  type MeetingSearchResponse,
  type MeetingsListResponse,
  type TranslateMomResponse,
} from "./schemas";

// ----------------------------------------------------------------------
// Query keys
// ----------------------------------------------------------------------
export const companyMeetingsKeys = {
  all: ["company", "meetings"] as const,
  list: (params: CompanyMeetingsListParams) =>
    ["company", "meetings", "list", params] as const,
  detail: (id: string) => ["company", "meetings", "detail", id] as const,
  /** Notes attached to a meeting. ONE key for both pages that share
   *  `MeetingDetailView` — unlike the meeting itself, this is read-only here,
   *  so there is no mutation to keep two caches in step. Exported because the
   *  notes feature attaches and detaches, and has to invalidate it. */
  attachedNotes: (id: string) =>
    ["company", "meetings", "attached-notes", id] as const,
};

// ----------------------------------------------------------------------
// Ask your meetings (03-08-2026)
// A mutation rather than a query: asking is an ACTION with a cost (it consumes
// the plan's search allowance), so it must fire when the user submits — never
// on mount, focus, or a cache refetch, which is exactly what useQuery would do.
// Related: BACKEND meetingSearch.service.mjs, meetingAnswer.service.mjs
// ----------------------------------------------------------------------
/**
 * The remaining search allowance, read WITHOUT spending one.
 *
 * The count already rode along inside every search response, so the only way to
 * learn how many you had left was to use one — backwards for a metered feature,
 * where the number matters most before you decide to ask. A plain query, unlike
 * `useAskMeetings`, because reading the allowance costs nothing.
 */
export function useMeetingSearchQuota() {
  return useQuery({
    queryKey: ["meeting-search-quota"],
    queryFn: async () => {
      const raw = await apiGet<unknown>(boardroomEndpoints.searchQuota);
      return MeetingSearchQuotaResponseSchema.parse(raw);
    },
    staleTime: 60_000,
  });
}

export function useAskMeetings() {
  const queryClient = useQueryClient();
  return useMutation<
    MeetingSearchResponse,
    unknown,
    { q: string; meetingId?: string | null; answer?: boolean; limit?: number }
  >({
    mutationFn: async (vars) => {
      const raw = await apiPost<unknown>(boardroomEndpoints.search, {
        q: vars.q,
        meetingId: vars.meetingId ?? undefined,
        answer: vars.answer ?? true,
        limit: vars.limit,
      });
      return MeetingSearchResponseSchema.parse(raw);
    },
    // A search spends one, so the standalone allowance read is now stale. The
    // response itself carries a fresher count for this render; this keeps the
    // number right after a remount too.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meeting-search-quota"] });
    },
  });
}

// ----------------------------------------------------------------------
// Query string builder
// Boardroom uses page/pageSize/search (not limit/offset/q like sessions).
// employee_id narrows to a single owner (backend gates it to company admins).
// ----------------------------------------------------------------------
function buildQuery(params: CompanyMeetingsListParams): string {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;
  const usp = new URLSearchParams();
  usp.set("page", String(page));
  usp.set("pageSize", String(pageSize));
  if (params.search) usp.set("search", params.search);
  if (params.from) usp.set("from", params.from);
  if (params.to) usp.set("to", params.to);
  if (params.employee_id) usp.set("employee_id", params.employee_id);
  if (params.tier) usp.set("tier", params.tier);
  if (params.scope) usp.set("scope", params.scope);
  return usp.toString();
}

// ----------------------------------------------------------------------
// List — GET /api/boardroom/sessions
// ----------------------------------------------------------------------
export function useCompanyMeetingsList(
  params: CompanyMeetingsListParams,
  options?: Partial<UseQueryOptions<MeetingsListResponse>>,
) {
  return useQuery<MeetingsListResponse>({
    queryKey: companyMeetingsKeys.list(params),
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        `${boardroomEndpoints.list}?${buildQuery(params)}`,
      );
      return MeetingsListResponseSchema.parse(raw);
    },
    placeholderData: (prev) => prev,
    ...options,
  });
}

// ----------------------------------------------------------------------
// Detail — GET /api/boardroom/sessions/:id
// ----------------------------------------------------------------------
export function useCompanyMeetingDetail(id: string | null, enabled = true) {
  return useQuery<MeetingDetail>({
    queryKey: companyMeetingsKeys.detail(id ?? ""),
    enabled: enabled && Boolean(id),
    queryFn: async () => {
      const raw = await apiGet<unknown>(boardroomEndpoints.byId(id!));
      return MeetingDetailResponseSchema.parse(raw).meeting;
    },
    // Poll while diarization/synthesis runs so the detail flips to the fresh
    // result on its own — regenerate + reprocess are background jobs now, so
    // the page must not require a manual refresh. (08-07-2026)
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "processing" || s === "synthesis_processing" ? 3000 : false;
    },
  });
}

// ----------------------------------------------------------------------
// Attached notes — GET /api/boardroom/sessions/:id/prep-notes (21-08-2026)
//
// Personal notes the OWNER attached to this meeting, read from the meeting's
// side (ADR-023). Owner-only on the server, reads included, so a company admin
// looking at a member's meeting gets a 404 — resolved here to an empty list, so
// the section is simply absent rather than the page showing an error for a
// perfectly normal permission outcome.
// ----------------------------------------------------------------------
export function useMeetingAttachedNotes(id: string | null, enabled = true) {
  return useQuery<MeetingAttachedNote[]>({
    queryKey: companyMeetingsKeys.attachedNotes(id ?? ""),
    enabled: enabled && Boolean(id),
    queryFn: async () => {
      try {
        const raw = await apiGet<unknown>(boardroomEndpoints.prepNotes(id!));
        return MeetingAttachedNotesResponseSchema.parse(raw).notes ?? [];
      } catch {
        return [];
      }
    },
    staleTime: 60_000,
  });
}

// ----------------------------------------------------------------------
// Pagination meta derivation (parity with company-sessions)
// ----------------------------------------------------------------------
export function companyMeetingsPagination(
  data: MeetingsListResponse | undefined,
  params: CompanyMeetingsListParams,
) {
  const pageSize = params.pageSize ?? 20;
  const page = params.page ?? 1;
  const total = Number(data?.total ?? 0);
  return {
    page,
    limit: pageSize,
    total,
    totalPages: Math.max(1, Math.ceil(total / pageSize)),
  };
}

// ----------------------------------------------------------------------
// Toggle one action item (04-08-2026) — web parity with the mobile checklist.
// PATCH one todo, never the whole array: see the endpoint's own note.
// Related: MeetingDetailView.tsx (ActionItemRow),
//          FRONTEND/.../MeetingDetails/hooks/useMeetingDetailsContent.ts
// ----------------------------------------------------------------------
export type ToggleTodoVars = {
  meetingId: string;
  todoId: string;
  completed: boolean;
};

/**
 * The SAME meeting is cached under two different keys — `["company","meetings",
 * "detail",id]` for the company page and `["admin","meetings","detail",id]` for
 * the super-admin one — because each page has its own read hook. `MeetingDetailView`
 * is shared between them and renders whichever the page passed in, so a mutation
 * that patched only one key left the other page's tick doing nothing visible
 * until a manual reload. Both are updated here; a stale entry under the other
 * key IS the bug. (04-08-2026)
 */
export function meetingDetailKeys(meetingId: string) {
  return [
    companyMeetingsKeys.detail(meetingId),
    adminMeetingsKeys.detail(meetingId),
  ];
}

/** People a manual to-do may be assigned to — participant or not. */
export function useAssignablePeople(enabled = true) {
  return useQuery({
    queryKey: ["assignable-people"],
    queryFn: async () => {
      const raw = (await apiGet(boardroomEndpoints.assignablePeople)) as {
        people?: { id: string; name?: string; email?: string; avatarUrl?: string | null }[];
      };
      return raw.people ?? [];
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}

// ----------------------------------------------------------------------
// Transcript editing (29-09-2026): web parity with the app's press-and-hold
// "Correct" and "Who said this?". Owner-only server-side. Both answer with
// the whole meeting (the intel may have changed too), written to BOTH detail
// keys; `message` is set when the transcript saved but the intel pass failed.
// Related: BACKEND meetingCorrections.service.mjs, meetingSpeakerMove.service.mjs
// ----------------------------------------------------------------------
export type TranscriptEditResult = { meeting: MeetingDetail; message?: string };

async function postTranscriptEdit(url: string, body: unknown): Promise<TranscriptEditResult> {
  const raw = (await apiPost<unknown>(url, body)) as { message?: unknown };
  const meeting = MeetingDetailResponseSchema.parse(raw).meeting;
  return {
    meeting,
    message: typeof raw?.message === "string" ? raw.message : undefined,
  };
}

export function useCorrectMeetingTranscript() {
  const qc = useQueryClient();
  return useMutation<
    TranscriptEditResult,
    Error,
    {
      meetingId: string;
      segmentId: string;
      from: string;
      to: string;
      scope: "one" | "all";
      offset: number;
    }
  >({
    mutationFn: ({ meetingId, ...body }) =>
      postTranscriptEdit(boardroomEndpoints.corrections(meetingId), body),
    onSuccess: ({ meeting }, { meetingId }) => {
      for (const key of meetingDetailKeys(meetingId)) qc.setQueryData(key, meeting);
    },
  });
}

export type SpeakerMoveTarget =
  | { speakerId: string }
  | {
      name: string;
      email?: string;
      isExternal?: boolean;
      profileImageUrl?: string | null;
      isRegisteredUser?: boolean;
    };

export function useMoveTranscriptSpeaker() {
  const qc = useQueryClient();
  return useMutation<
    TranscriptEditResult,
    Error,
    {
      meetingId: string;
      segmentId: string;
      selectedText: string;
      start: number;
      to: SpeakerMoveTarget;
    }
  >({
    mutationFn: ({ meetingId, ...body }) =>
      postTranscriptEdit(boardroomEndpoints.speakerMoves(meetingId), body),
    onSuccess: ({ meeting }, { meetingId }) => {
      for (const key of meetingDetailKeys(meetingId)) qc.setQueryData(key, meeting);
    },
  });
}

// ----------------------------------------------------------------------
// Change a whole speaker (02-10-2026): every line, to-do and mention of one
// voice becomes another person; no AI. Owner or company admin server-side.
// `to`: another voice here, a colleague (`userId`), or someone else.
// Related: BACKEND meetingSpeakerSwap.service.mjs
// ----------------------------------------------------------------------
export type SpeakerSwapTarget =
  | { speakerId: string }
  | { userId: string }
  | { name: string; email?: string; isExternal?: boolean };

export type SpeakerSwapResult = {
  meeting: MeetingDetail;
  undoAvailable: boolean;
  swap: { id: string; from: { name: string }; to: { name: string } } | null;
};

export function useSwapMeetingSpeaker() {
  const qc = useQueryClient();
  return useMutation<
    SpeakerSwapResult,
    Error,
    { meetingId: string; fromSpeakerId: string; to: SpeakerSwapTarget }
  >({
    mutationFn: async ({ meetingId, ...body }) => {
      const raw = (await apiPost<unknown>(boardroomEndpoints.speakerSwap(meetingId), body)) as {
        undoAvailable?: unknown;
        swap?: SpeakerSwapResult["swap"];
      };
      return {
        meeting: MeetingDetailResponseSchema.parse(raw).meeting,
        undoAvailable: raw?.undoAvailable === true,
        swap: raw?.swap ?? null,
      };
    },
    onSuccess: ({ meeting }, { meetingId }) => {
      for (const key of meetingDetailKeys(meetingId)) qc.setQueryData(key, meeting);
    },
  });
}

export function useUndoSpeakerSwap() {
  const qc = useQueryClient();
  return useMutation<MeetingDetail, Error, { meetingId: string; swapId: string }>({
    mutationFn: async ({ meetingId, swapId }) => {
      const raw = await apiPost<unknown>(boardroomEndpoints.speakerSwapUndo(meetingId), { swapId });
      return MeetingDetailResponseSchema.parse(raw).meeting;
    },
    onSuccess: (meeting, { meetingId }) => {
      for (const key of meetingDetailKeys(meetingId)) qc.setQueryData(key, meeting);
    },
  });
}

export function useAddMeetingTodo() {
  const qc = useQueryClient();
  return useMutation<
    MeetingDetail,
    Error,
    {
      meetingId: string;
      task: string;
      assigneeUserId?: string | null;
      dueDate?: string | null;
      /** B48 "Add someone": a person outside the lists, name + email. */
      newAssignee?: { name: string; email: string };
    }
  >({
    mutationFn: async ({ meetingId, ...body }) => {
      const raw = await apiPost<unknown>(boardroomEndpoints.addTodo(meetingId), body);
      return MeetingDetailResponseSchema.parse(raw).meeting;
    },
    // No optimistic insert: the server mints the id and resolves the assignee,
    // so a placeholder row would have to be reconciled anyway — and a to-do
    // that appears and then changes reads as a bug.
    onSuccess: (meeting, { meetingId }) => {
      for (const key of meetingDetailKeys(meetingId)) qc.setQueryData(key, meeting);
    },
  });
}

/**
 * Edit a to-do, owner only. A manual one: text, person, due date. One from
 * the recording (B48, 02-11-2026, "Edit to-do"): its wording and due date may
 * change too; the server keeps the first AI wording as `originalTask` and the
 * evidence anchor untouched, so the link to what was said stays true. Send
 * the assignee keys only when the person changes: a body with just `task` /
 * `dueDate` keeps the person, an empty body still means Unassigned.
 */
export function useUpdateMeetingTodo() {
  const qc = useQueryClient();
  return useMutation<
    MeetingDetail,
    Error,
    {
      meetingId: string;
      todoId: string;
      task?: string;
      assigneeUserId?: string | null;
      /** AI to-dos only: "team", or a speaker of this meeting. */
      assigneeSpeakerId?: string | null;
      dueDate?: string | null;
      /** B48 "Add someone": a person outside the lists, name + email. */
      newAssignee?: { name: string; email: string };
    }
  >({
    mutationFn: async ({ meetingId, todoId, ...body }) => {
      const raw = await apiPut<unknown>(
        boardroomEndpoints.updateTodo(meetingId, todoId),
        body,
      );
      return MeetingDetailResponseSchema.parse(raw).meeting;
    },
    // No optimistic patch: the server re-resolves the assignee from the company
    // roster, so a local guess would have to be reconciled anyway.
    onSuccess: (meeting, { meetingId }) => {
      for (const key of meetingDetailKeys(meetingId)) qc.setQueryData(key, meeting);
    },
  });
}

/**
 * Rename a meeting from its page (B48, 02-11-2026). Owner only (PATCH
 * /sessions/:id title; trimmed, 1-200 characters). The linked scheduled
 * meeting follows on the server; here the detail caches take the new title
 * and every list is refetched.
 */
export function useRenameMeeting() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { meetingId: string; title: string }>({
    mutationFn: ({ meetingId, title }) =>
      apiPatch<unknown>(boardroomEndpoints.byId(meetingId), { title }),
    onSuccess: (_res, { meetingId, title }) => {
      for (const key of meetingDetailKeys(meetingId)) {
        qc.setQueryData<MeetingDetail | undefined>(key, (prev) => (prev ? { ...prev, title } : prev));
      }
      void qc.invalidateQueries({ queryKey: companyMeetingsKeys.all });
      void qc.invalidateQueries({ queryKey: adminMeetingsKeys.all });
      void qc.invalidateQueries({ queryKey: ["my-todos"] });
    },
  });
}

/**
 * "Add email" for a guest who has a name only (B48). Owner only. Writes the
 * email on that guest's roster entry, speaker tracks and to-dos of this
 * meeting; grants no access and sends nothing.
 */
export function useAddGuestEmail() {
  const qc = useQueryClient();
  return useMutation<MeetingDetail, Error, { meetingId: string; name: string; email: string }>({
    mutationFn: async ({ meetingId, name, email }) => {
      const raw = await apiPost<unknown>(boardroomEndpoints.guestEmail(meetingId), { name, email });
      return MeetingDetailResponseSchema.parse(raw).meeting;
    },
    onSuccess: (meeting, { meetingId }) => {
      for (const key of meetingDetailKeys(meetingId)) qc.setQueryData(key, meeting);
    },
  });
}

export function useDeleteMeetingTodo() {
  const qc = useQueryClient();
  return useMutation<MeetingDetail, Error, { meetingId: string; todoId: string }>({
    mutationFn: async ({ meetingId, todoId }) => {
      const raw = await apiDelete<unknown>(
        boardroomEndpoints.deleteTodo(meetingId, todoId),
      );
      return MeetingDetailResponseSchema.parse(raw).meeting;
    },
    onSuccess: (meeting, { meetingId }) => {
      for (const key of meetingDetailKeys(meetingId)) qc.setQueryData(key, meeting);
    },
  });
}

// Every tag on a meeting the viewer can see, for the tags menu's suggestions
// (05-10-2026, phone parity: MeetingIntelTab loadMeetingTagCatalogue). The
// list route returns `available_tags`; one row is enough to get it. Fetched
// only while the menu is open.
export function useMeetingTagCatalogue(enabled: boolean) {
  return useQuery({
    queryKey: ["meeting-tag-catalogue"],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const raw = await apiGet<{ available_tags?: unknown }>(
        `${boardroomEndpoints.list}?page=1&pageSize=1`,
      );
      return Array.isArray(raw?.available_tags)
        ? raw.available_tags.filter((t): t is string => typeof t === "string")
        : [];
    },
  });
}

// Dismiss / restore an AI to-do (05-10-2026). Hidden, never deleted.
export function useDismissMeetingTodo() {
  const qc = useQueryClient();
  return useMutation<
    MeetingDetail,
    Error,
    { meetingId: string; todoId: string; dismissed: boolean }
  >({
    mutationFn: async ({ meetingId, todoId, dismissed }) => {
      const raw = await apiPut<unknown>(boardroomEndpoints.dismissTodo(meetingId, todoId), {
        dismissed,
      });
      return MeetingDetailResponseSchema.parse(raw).meeting;
    },
    onSuccess: (meeting, { meetingId }) => {
      for (const key of meetingDetailKeys(meetingId)) qc.setQueryData(key, meeting);
    },
  });
}

// Follow-up owner of an external to-do (05-10-2026). `userId: null` returns
// it to the meeting owner. Written to BOTH detail keys, like every to-do write.
export function useSetTodoFollowUp() {
  const qc = useQueryClient();
  return useMutation<
    MeetingDetail,
    Error,
    { meetingId: string; todoId: string; userId: string | null }
  >({
    mutationFn: async ({ meetingId, todoId, userId }) => {
      const raw = await apiPut<unknown>(boardroomEndpoints.todoFollowUp(meetingId, todoId), {
        userId,
      });
      return MeetingDetailResponseSchema.parse(raw).meeting;
    },
    onSuccess: (meeting, { meetingId }) => {
      for (const key of meetingDetailKeys(meetingId)) qc.setQueryData(key, meeting);
    },
  });
}

export function useToggleMeetingTodo() {
  const qc = useQueryClient();
  return useMutation<
    MeetingDetail | undefined,
    Error,
    ToggleTodoVars,
    { previous: Array<[readonly unknown[], MeetingDetail | undefined]> }
  >({
    mutationFn: async ({ meetingId, todoId, completed }) => {
      const raw = await apiPatch<unknown>(
        boardroomEndpoints.toggleTodo(meetingId, todoId),
        { completed },
      );
      // A to-do-only user gets `{ todo }` and no `meeting` (12-10-2026).
      return ToggleTodoResponseSchema.parse(raw).meeting;
    },
    // Optimistic flip — a checkbox that waits for a round-trip feels broken.
    onMutate: async ({ meetingId, todoId, completed }) => {
      const previous: Array<[readonly unknown[], MeetingDetail | undefined]> = [];
      for (const key of meetingDetailKeys(meetingId)) {
        await qc.cancelQueries({ queryKey: key });
        const prev = qc.getQueryData<MeetingDetail>(key);
        previous.push([key, prev]);
        if (!prev) continue;
        qc.setQueryData<MeetingDetail>(key, {
          ...prev,
          todos: (prev.todos ?? []).map((t) =>
            t.id === todoId
              ? {
                  ...t,
                  completed,
                  // Clear the stamp for the state we are moving INTO rather
                  // than guessing it locally: the server stamps off the DB
                  // clock, and a leftover value from an earlier toggle would
                  // flash the wrong time for the length of the round-trip.
                  // (mirrors the mobile controller)
                  ...(completed
                    ? { completedAt: null }
                    : { uncompletedAt: null }),
                }
              : t,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, prev] of ctx?.previous ?? []) {
        if (prev) qc.setQueryData(key, prev);
      }
    },
    // Trust the server's row (it carries the real stamps and `is_owner`), then
    // refresh both lists so their open/done counts follow.
    onSuccess: (meeting, vars) => {
      for (const key of meetingDetailKeys(vars.meetingId)) {
        // Only refresh a cache that already holds this meeting — seeding the
        // other page's key would invent an entry it never asked for. No
        // `meeting` in the reply (to-do-only user): refetch instead.
        if (!qc.getQueryData(key)) continue;
        if (meeting) qc.setQueryData(key, meeting);
        else void qc.invalidateQueries({ queryKey: key });
      }
      void qc.invalidateQueries({ queryKey: companyMeetingsKeys.all });
      void qc.invalidateQueries({ queryKey: adminMeetingsKeys.all });
      // Home's and /todos' "My to-dos" (12-10-2026). The literal key, not an
      // import: features/todos imports this file, never the other way round.
      void qc.invalidateQueries({ queryKey: ["my-todos"] });
    },
  });
}

// ----------------------------------------------------------------------
// Manage mutations
// Each invalidates the list + the affected detail so the UI reflects the
// new state. Backend authorizes company-admin scope server-side.
// ----------------------------------------------------------------------
export function useDeleteCompanyMeeting() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: async (id) => apiDelete<unknown>(boardroomEndpoints.delete(id)),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: companyMeetingsKeys.all });
      void qc.invalidateQueries({ queryKey: companyMeetingsKeys.detail(id) });
    },
  });
}

export function useReprocessCompanyMeetingDiarization() {
  const qc = useQueryClient();
  return useMutation<MeetingDetail, Error, string>({
    mutationFn: async (id) => {
      const raw = await apiPost<unknown>(
        boardroomEndpoints.reprocessDiarization(id),
        {},
      );
      return MeetingDetailResponseSchema.parse(raw).meeting;
    },
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: companyMeetingsKeys.all });
      void qc.invalidateQueries({ queryKey: companyMeetingsKeys.detail(id) });
    },
  });
}

// ----------------------------------------------------------------------
// Upgrade a Meeting Notes recording to full Intelligence (ADR-030, 06-10-2026)
// POST /sessions/:id/upgrade, owner only. Re-runs the full pipeline on the same
// recording, so on success the meeting is back at `processing` and then
// `resolved_pending` (speaker naming), exactly like a freshly recorded full
// meeting. Refusals (402 UPGRADE_NEEDS_MINUTES, 409 MEETING_BUSY /
// BILLING_PENDING / ALREADY_FULL) arrive as ApiClientError with a readable
// message to show as it is. Both detail caches are written (meetingDetailKeys)
// and every list is refreshed: the meeting leaves the Notes list and joins the
// Meetings list.
// Related: MeetingNotesTierView.tsx, BACKEND features/boardroom/routes.js
// ----------------------------------------------------------------------
export type UpgradeMeetingResult = { meeting: MeetingDetail; alreadyUpgraded: boolean };

export function useUpgradeMeetingToFull() {
  const qc = useQueryClient();
  return useMutation<UpgradeMeetingResult, unknown, string>({
    mutationFn: async (id) => {
      const raw = await apiPost<unknown>(boardroomEndpoints.upgrade(id), {});
      const parsed = MeetingDetailResponseSchema.extend({
        alreadyUpgraded: z.boolean().optional(),
      }).parse(raw);
      return { meeting: parsed.meeting, alreadyUpgraded: parsed.alreadyUpgraded === true };
    },
    onSuccess: ({ meeting }, id) => {
      for (const key of meetingDetailKeys(id)) qc.setQueryData(key, meeting);
      void qc.invalidateQueries({ queryKey: companyMeetingsKeys.all });
      void qc.invalidateQueries({ queryKey: adminMeetingsKeys.all });
    },
  });
}

export type RegenerateIntelligenceVars = {
  id: string;
  force?: boolean;
};

export function useRegenerateCompanyMeetingIntelligence() {
  const qc = useQueryClient();
  return useMutation<MeetingDetail, Error, RegenerateIntelligenceVars>({
    mutationFn: async ({ id, force = false }) => {
      const raw = await apiPost<unknown>(
        boardroomEndpoints.regenerateIntelligence(id),
        { force },
      );
      return MeetingDetailResponseSchema.parse(raw).meeting;
    },
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: companyMeetingsKeys.all });
      void qc.invalidateQueries({ queryKey: companyMeetingsKeys.detail(id) });
    },
  });
}

export type ExportMomVars = {
  id: string;
  regenerate?: boolean;
  targetLanguage?: string;
};

export function useExportCompanyMeetingMom() {
  return useMutation<ExportMomResponse, Error, ExportMomVars>({
    mutationFn: async ({ id, regenerate = false, targetLanguage }) => {
      const raw = await apiPost<unknown>(
        boardroomEndpoints.exportMom(id),
        {
          regenerate,
          ...(targetLanguage ? { targetLanguage } : {}),
        },
        // MoM PDF + optional MT can exceed the default 30s client timeout.
        { timeout: 180_000 },
      );
      return ExportMomResponseSchema.parse(raw);
    },
  });
}

/** Transcript PDF = original spoken language, no target language
 *  (DES 15-10-2026, ADR-037). */
export type ExportTranscriptVars = {
  id: string;
  regenerate?: boolean;
};

export function useExportCompanyMeetingTranscript() {
  return useMutation<ExportTranscriptResponse, Error, ExportTranscriptVars>({
    mutationFn: async ({ id, regenerate = false }) => {
      const raw = await apiPost<unknown>(
        boardroomEndpoints.exportTranscript(id),
        { regenerate },
        // Chromium PDF render on a long meeting can exceed the default 30s.
        { timeout: 180_000 },
      );
      return ExportTranscriptResponseSchema.parse(raw);
    },
  });
}

export type TranslateMomVars = {
  id: string;
  targetLanguage: string;
  scope?: "all" | "prose" | "decisions" | "keyPoints" | "todos";
};

export function useTranslateCompanyMeetingMom() {
  const qc = useQueryClient();
  return useMutation<TranslateMomResponse, Error, TranslateMomVars>({
    mutationFn: async ({ id, targetLanguage, scope = "all" }) => {
      const raw = await apiPost<unknown>(
        boardroomEndpoints.translate(id),
        { targetLanguage, scope },
        // MoM MT batches can exceed the default 30s (mobile uses 90s).
        { timeout: 180_000 },
      );
      return TranslateMomResponseSchema.parse(raw);
    },
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: companyMeetingsKeys.detail(id) });
    },
  });
}
